import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { Spinner } from './Spinner'

type Variant = 'ink' | 'accent' | 'ghost' | 'danger'
type Size = 'md' | 'lg'

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  children: ReactNode
  variant?: Variant
  size?: Size
  loading?: boolean
  block?: boolean
  icon?: ReactNode
}

/**
 * The button.
 *
 * **Ink is the primary variant, not the accent.** That is the design system's
 * first borrowed rule: dark ink buttons keep the coral free to mean "look here"
 * rather than "this is a button". `accent` exists for the one action on a screen
 * that must be found instantly, and carries `--on-accent` — near-black content
 * on coral, which is the pairing that clears 4.5:1 (white measures 3.08:1 and
 * was rejected in SUAP-0102).
 *
 * `loading` keeps the label and puts a spinner in the icon slot: a button that
 * swaps its label for a spinner changes width mid-press, which moves the thing
 * under the user's thumb. Clicks are refused while loading rather than queued.
 *
 * Every variant is at least 44px tall (design system § Shape and space).
 *
 * SUAP-0103.
 */
const VARIANTS: Record<Variant, string> = {
  ink: 'bg-ink text-ground border border-ink',
  accent: 'bg-accent text-on-accent border border-accent',
  ghost: 'bg-transparent text-ink border border-hairline',
  danger: 'bg-transparent text-over border border-over',
}

const SIZES: Record<Size, string> = {
  md: 'min-h-11 px-4 text-sm',
  lg: 'min-h-14 px-6 text-base',
}

export function Button({
  children,
  variant = 'ink',
  size = 'md',
  loading = false,
  block = false,
  icon,
  disabled,
  onClick,
  type = 'button',
  ...rest
}: ButtonProps) {
  const inactive = disabled === true || loading

  return (
    <button
      {...rest}
      type={type}
      disabled={inactive}
      aria-busy={loading || undefined}
      onClick={loading ? undefined : onClick}
      className={[
        'inline-flex items-center justify-center gap-2 rounded-chip font-semibold',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
        'motion-safe:transition-opacity',
        // Opacity rather than a muted palette: a disabled variant per colour is
        // four more token pairs nobody checked, and 45% of a passing pair is
        // still legible as "not available" without pretending to be readable.
        //
        // **Loading is not disabled**, visually. Both refuse clicks, but a
        // button that is working should look like it is working — fading it to
        // 45% makes the spinner the hardest thing on the button to see, which
        // is the opposite of what it is for. Seen in the first `/ui-kit`
        // screenshot (SUAP-0103) and fixed there.
        disabled === true ? 'opacity-45' : loading ? '' : 'motion-safe:active:opacity-80',
        VARIANTS[variant],
        SIZES[size],
        block ? 'w-full' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {loading ? <Spinner size={size === 'lg' ? 22 : 18} label={null} /> : icon}
      <span className="truncate">{children}</span>
    </button>
  )
}
